import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Server, 
  Crown, 
  DollarSign,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import toast from 'react-hot-toast';

const ServerSubscriptionManagement = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    planType: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Fetch all server subscriptions
  const { data: subscriptionsData, isLoading, error } = useQuery(
    ['adminServerSubscriptions', searchTerm, filters],
    () => adminAPI.getAllServerSubscriptions({
      search: searchTerm,
      ...filters
    }).then(res => res.data),
    { enabled: true }
  );

  // Fetch subscription analytics
  const { data: analyticsData } = useQuery(
    'subscriptionAnalytics',
    () => adminAPI.getSubscriptionAnalytics().then(res => res.data)
  );

  const subscriptions = subscriptionsData?.data?.subscriptions || [];
  const analytics = analyticsData?.data?.analytics || {};

  // Mutations
  const cancelSubscriptionMutation = useMutation(
    (subscriptionId) => adminAPI.cancelServerSubscription(subscriptionId),
    {
      onSuccess: () => {
        toast.success('Subscription cancelled successfully');
        queryClient.invalidateQueries('adminServerSubscriptions');
        queryClient.invalidateQueries('subscriptionAnalytics');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to cancel subscription');
      }
    }
  );

  const reactivateSubscriptionMutation = useMutation(
    (subscriptionId) => adminAPI.reactivateServerSubscription(subscriptionId),
    {
      onSuccess: () => {
        toast.success('Subscription reactivated successfully');
        queryClient.invalidateQueries('adminServerSubscriptions');
        queryClient.invalidateQueries('subscriptionAnalytics');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reactivate subscription');
      }
    }
  );

  const handleCancelSubscription = (subscriptionId) => {
    if (window.confirm('Are you sure you want to cancel this subscription?')) {
      cancelSubscriptionMutation.mutate(subscriptionId);
    }
  };

  const handleReactivateSubscription = (subscriptionId) => {
    reactivateSubscriptionMutation.mutate(subscriptionId);
  };

  const getStatusBadge = (status) => {
    const normalized = status === 'cancelled' ? 'canceled' : status;
    const statusConfig = {
      active: { color: 'bg-green-500', text: 'Active', icon: CheckCircle },
      canceled: { color: 'bg-red-500', text: 'Canceled', icon: XCircle },
      past_due: { color: 'bg-yellow-500', text: 'Past Due', icon: AlertTriangle },
      trialing: { color: 'bg-blue-500', text: 'Trialing', icon: RefreshCw }
    };

    const config = statusConfig[normalized] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium text-white ${config.color}`}>
        <Icon className="h-3 w-3" />
        <span>{config.text}</span>
      </span>
    );
  };

  const getPlanBadge = (planType) => {
    const styleByPlan = {
      free: { cls: 'bg-discord-dark text-discord-light', label: 'Free' },
      premium: { cls: 'bg-discord-blurple text-white', label: 'Premium' },
      pro: { cls: 'bg-purple-600 text-white', label: 'Pro' },
      enterprise: { cls: 'bg-gray-800 text-white', label: 'Enterprise' }
    };
    const cfg = styleByPlan[planType] || styleByPlan.free;
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.cls}`}>
        <Crown className="h-3 w-3" />
        <span>{cfg.label}</span>
      </span>
    );
  };

  if (isLoading) {
    return <LoadingSpinner size="xl" />;
  }

  if (error) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-discord-lightest mb-2">Error Loading Subscriptions</h3>
          <p className="text-discord-light">Failed to load server subscriptions. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-discord-light">Total Subscriptions</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {analytics.totalSubscriptions || 0}
              </p>
            </div>
            <Server className="h-8 w-8 text-discord-blurple" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-discord-light">Active Subscriptions</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {analytics.activeSubscriptions || 0}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-discord-light">Premium Servers</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {analytics.premiumSubscriptions || 0}
              </p>
            </div>
            <Crown className="h-8 w-8 text-discord-yellow" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-discord-light">Monthly Revenue</p>
              <p className="text-2xl font-bold text-discord-lightest">
                ${analytics.monthlyRevenue || 0}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-discord-light" />
              <input
                type="text"
                placeholder="Search by server name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-discord-dark border border-discord-darker rounded-lg text-discord-lightest placeholder-discord-light focus:outline-none focus:ring-2 focus:ring-discord-blurple focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 bg-discord-dark border border-discord-darker rounded-lg text-discord-lightest focus:outline-none focus:ring-2 focus:ring-discord-blurple"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
              <option value="past_due">Past Due</option>
              <option value="trialing">Trialing</option>
            </select>

            <select
              value={filters.planType}
              onChange={(e) => setFilters({ ...filters, planType: e.target.value })}
              className="px-3 py-2 bg-discord-dark border border-discord-darker rounded-lg text-discord-lightest focus:outline-none focus:ring-2 focus:ring-discord-blurple"
            >
              <option value="">All Plans</option>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
            </select>
          </div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-discord-darker">
                <th className="text-left py-3 px-4 font-semibold text-discord-lightest">Server</th>
                <th className="text-left py-3 px-4 font-semibold text-discord-lightest">Plan</th>
                <th className="text-left py-3 px-4 font-semibold text-discord-lightest">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-discord-lightest">Games</th>
                <th className="text-left py-3 px-4 font-semibold text-discord-lightest">Period</th>
                <th className="text-left py-3 px-4 font-semibold text-discord-lightest">Owner</th>
                <th className="text-left py-3 px-4 font-semibold text-discord-lightest">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((subscription) => (
                <tr key={subscription._id} className="border-b border-discord-darker/50 hover:bg-discord-darker/30">
                  <td className="py-4 px-4">
                    <div>
                      <p className="font-medium text-discord-lightest">{subscription.serverName}</p>
                      <p className="text-sm text-discord-light">ID: {subscription.serverId}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    {getPlanBadge(subscription.planType)}
                  </td>
                  <td className="py-4 px-4">
                    {getStatusBadge(subscription.status)}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex flex-wrap gap-1">
                      {subscription.availableGames?.map((game) => (
                        <span key={game} className="px-2 py-1 bg-discord-blurple/20 text-discord-blurple text-xs rounded">
                          {game.replace('-', ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    {subscription.currentPeriodEnd ? (
                      <div>
                        <p className="text-sm text-discord-lightest">
                          Until {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                        </p>
                        {subscription.cancelAtPeriodEnd && (
                          <p className="text-xs text-yellow-500">Cancels at period end</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-discord-light">N/A</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <div>
                      <p className="text-sm text-discord-lightest">User ID: {subscription.ownerId}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      {subscription.status === 'active' && (
                        <button
                          onClick={() => handleCancelSubscription(subscription._id)}
                          className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Cancel Subscription"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      )}
                      {subscription.status === 'cancelled' && (
                        <button
                          onClick={() => handleReactivateSubscription(subscription._id)}
                          className="p-2 text-green-500 hover:bg-green-500/20 rounded-lg transition-colors"
                          title="Reactivate Subscription"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {subscriptions.length === 0 && (
            <div className="text-center py-8">
              <Server className="h-12 w-12 text-discord-light mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-discord-lightest mb-2">No Subscriptions Found</h3>
              <p className="text-discord-light">
                {searchTerm ? 'Try adjusting your search criteria.' : 'No server subscriptions have been created yet.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServerSubscriptionManagement;
